import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MantineProvider, createTheme } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';

import App from './App';
import { msalConfig } from './auth/authConfig';
import { LocalAuthProvider } from './auth/local-auth';
import './i18n/i18n';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/charts/styles.css';
import './styles/global.css';

const ENABLE_MOCKS = import.meta.env.VITE_ENABLE_MOCKS === 'true';
// MSAL solo se mockea si la build se hizo con VITE_ENABLE_MOCKS=true.
// El login unificado (email+password y/o Continuar con Microsoft) vive en /login.
const USE_MOCK_AUTH = ENABLE_MOCKS;

async function bootstrap() {
  // Si MSW está desactivado pero quedó un service worker registrado de una
  // sesión anterior con MSW=true, hay que desregistrarlo. Si no, el SW
  // viejo intercepta requests y las rompe (Failed to fetch).
  if (!ENABLE_MOCKS && 'serviceWorker' in navigator) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    } catch {
      // best-effort: no bloquear el arranque si falla
    }
  }

  if (ENABLE_MOCKS) {
    const { worker } = await import('./mocks/browser');
    await worker.start({ onUnhandledRequest: 'bypass' });
    // eslint-disable-next-line no-console
    console.info('🧪 MSW activo: API mockeada en el navegador.');
  }

  // Crear instancia de MSAL. En modo mock la monkey-patcheamos con una
  // cuenta y token ficticios, así no necesitas App Registration real.
  const msalInstance = USE_MOCK_AUTH
    ? (await import('./mocks/msal-mock-instance')).createMockMsalInstance(msalConfig)
    : new PublicClientApplication(msalConfig);

  if (USE_MOCK_AUTH) {
    // eslint-disable-next-line no-console
    console.info('🔧 MSAL en modo MOCK (?mockAuth=true): usando cuenta demo@deca.local');
  }

  const theme = createTheme({
    primaryColor: 'deca',
    defaultRadius: 'md',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
    // Paleta verde unificada con la landing (`#0d5b5d` en CSS variables)
    // y con el frontend del conductor. Cualquier página que use `color="deca.X"`
    // (botones, alerts, badges, focus rings…) sale verde sin tocar nada más.
    // El shade 6 es el "principal" para Mantine (`primaryShade` por defecto).
    colors: {
      deca: [
        '#e6f3ef', // 0 — bg suaves, hover leve
        '#cfe8df', // 1
        '#b4dace', // 2
        '#92c8ba', // 3
        '#6eaf9f', // 4
        '#4e9788', // 5
        '#0d5b5d', // 6 — landing --color-deca (principal)
        '#084f50', // 7
        '#08494a', // 8
        '#084446', // 9 — landing --color-deca-dark (hover, focus)
      ],
    },
  });

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <MantineProvider theme={theme} defaultColorScheme="light">
            <Notifications position="top-right" />
            <LocalAuthProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </LocalAuthProvider>
          </MantineProvider>
        </QueryClientProvider>
      </MsalProvider>
    </React.StrictMode>,
  );
}

void bootstrap();
